import axios from 'axios';
import Cookies from 'js-cookie';

const api = axios.create({
  baseURL: process.env.REACT_APP_Back_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: attach JWT access token to every request
api.interceptors.request.use(
  (config) => {
    const accessToken = Cookies.get('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 by refreshing token, then retry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't retried yet, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = Cookies.get('refreshToken');
      if (!refreshToken) {
        // No refresh token — force logout
        clearAuthCookies();
        window.location.href = '/sign-in';
        return Promise.reject(error);
      }

      try {
        const refreshResponse = await axios.post(
          `${process.env.REACT_APP_Back_URL}/userst/refresh-token`,
          {},
          { headers: { refresh_token: refreshToken } }
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = refreshResponse.data;

        Cookies.set('accessToken', newAccessToken, { expires: 1, sameSite: 'Strict', secure: isProduction });
        if (newRefreshToken) {
          Cookies.set('refreshToken', newRefreshToken, { expires: 7, sameSite: 'Strict', secure: isProduction });
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed — force logout
        clearAuthCookies();
        window.location.href = '/sign-in';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Helper: store auth tokens after login
const isProduction = window.location.protocol === 'https:';
export function setAuthCookies(accessToken, refreshToken, userId) {
  Cookies.set('accessToken', accessToken, { expires: 1, sameSite: 'Strict', secure: isProduction });
  Cookies.set('refreshToken', refreshToken, { expires: 7, sameSite: 'Strict', secure: isProduction });
  Cookies.set('userId', userId, { expires: 7, sameSite: 'Strict', secure: isProduction });
  // Keep legacy 'token' cookie removed
  Cookies.remove('token');
}

// Helper: clear all auth cookies on logout
export function clearAuthCookies() {
  Cookies.remove('accessToken');
  Cookies.remove('refreshToken');
  Cookies.remove('userId');
  Cookies.remove('token');
}

// Helper: check if user is authenticated
export function isAuthenticated() {
  return !!Cookies.get('accessToken');
}

// Helper: get current userId
export function getUserId() {
  return Cookies.get('userId');
}

export default api;
